import React, { useState, useEffect } from 'react';
import { notificationService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardDescription,
  NativeCardContent,
  NativeInput,
  NativeAlert
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Bell,
  Zap,
  CreditCard,
  AlertTriangle,
  Info,
  Mail,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notificationService.getSettings();
      setSettings(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleToggle = async (key, value) => {
    const previousSettings = { ...settings };
    setSettings(prev => ({ ...prev, [key]: value }));
    
    try {
      await notificationService.updateSettings({ [key]: value });
      toast.success('Ayar guncellendi');
    } catch (err) {
      setSettings(previousSettings);
      toast.error(getErrorMessage(err));
    }
  };

  const handleThresholdChange = async () => {
    setSaving(true);
    try {
      await notificationService.updateSettings({
        low_balance_threshold: settings.low_balance_threshold
      });
      toast.success('Bakiye esigi guncellendi');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchSettings}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  if (!settings) {
    return (
      <NativeAlert variant="warning" icon={AlertTriangle}>
        Bildirim ayarları yüklenemedi
      </NativeAlert>
    );
  }

  // Native Switch Component
  const NativeSwitch = ({ checked, onChange, id }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        backgroundColor: checked ? '#2563eb' : '#d1d5db',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
      }}
    >
      <span style={{
        position: 'absolute',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: checked ? 'white' : '#f1f5f9',
        top: '2px',
        left: checked ? '22px' : '2px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
    </button>
  );

  const SettingItem = ({ icon: Icon, title, description, settingKey }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--card-border)' }} className="dark:border-slate-700">
      <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
        <div style={{ padding: '8px', backgroundColor: '#f1f5f9', borderRadius: '8px', height: 'fit-content' }} className="dark:bg-slate-800">
          <Icon style={{ width: '20px', height: '20px' }} className="text-slate-600 dark:text-slate-400" />
        </div>
        <div>
          <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', margin: 0 }}>{title}</p>
          <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', margin: '4px 0 0' }}>{description}</p>
        </div>
      </div>
      <NativeSwitch
        id={settingKey}
        checked={settings[settingKey]}
        onChange={(value) => handleToggle(settingKey, value)}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="notification-settings-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Bildirim Ayarlari</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Bildirim tercihlerinizi yönetin</p>
        </div>
        <NativeButton variant="outline" onClick={fetchSettings}>
          <RefreshCw style={{ width: '16px', height: '16px' }} />
          Yenile
        </NativeButton>
      </div>

      {/* Notification Types */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Bell}>Bildirim Turleri</NativeCardTitle>
          <NativeCardDescription>Hangi bildirimleri almak istediginizi secin</NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          <SettingItem
            icon={Zap}
            title="Şarj Bildirimleri"
            description="Şarj oturumu başladığında ve bittiğinde bildirim alın"
            settingKey="charging_notifications"
          />
          <SettingItem
            icon={CreditCard}
            title="Odeme Bildirimleri"
            description="Odeme islemleri hakkinda bildirim alin"
            settingKey="payment_notifications"
          />
          <SettingItem
            icon={AlertTriangle}
            title="Dusuk Bakiye Uyarilari"
            description="RFID kart bakiyeniz dusuk oldugunda uyari alin"
            settingKey="low_balance_alerts"
          />
          <SettingItem
            icon={Info}
            title="Sistem Duyurulari"
            description="Onemli sistem duyurularini alin"
            settingKey="system_announcements"
          />
        </NativeCardContent>
      </NativeCard>

      {/* Balance Threshold */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={CreditCard}>Bakiye Esigi</NativeCardTitle>
          <NativeCardDescription>Dusuk bakiye uyarisi icin esik degerini belirleyin</NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, maxWidth: '200px' }}>
              <NativeInput
                label="Esik Degeri (TL)"
                id="low_balance_threshold"
                type="number"
                min="0"
                step="1"
                value={settings.low_balance_threshold || 0}
                onChange={(e) => setSettings(prev => ({ ...prev, low_balance_threshold: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <NativeButton onClick={handleThresholdChange} disabled={saving} style={{ marginBottom: '12px' }}>
              {saving ? <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} /> : <Save style={{ width: '16px', height: '16px' }} />}
              Kaydet
            </NativeButton>
          </div>
          <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '13px', marginTop: '8px' }}>
            RFID kart bakiyeniz bu degerin altina dustugunde bildirim alirsiniz
          </p>
        </NativeCardContent>
      </NativeCard>

      {/* Email Settings */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Mail}>E-posta Bildirimleri</NativeCardTitle>
          <NativeCardDescription>E-posta bildirim tercihleri</NativeCardDescription>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          <SettingItem
            icon={Mail}
            title="E-posta Bildirimleri"
            description="Bildirimlerin e-posta ile gonderilmesini etkinlestirin"
            settingKey="email_enabled"
          />
        </NativeCardContent>
      </NativeCard>
    </div>
  );
}
